/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { MicroLabel, PanelEmptyState, SurfaceCard } from './sale-detail-atoms';
import type { SaleParty } from '../types/sale.types';

export interface SalePartiesPanelProps {
  /** Resolved by the server from the sale's `buyer_client_id`. */
  buyerName?: string;
  sellers?: SaleParty[];
}

/**
 * Who bought and who sold.
 *
 * The sellers are best-effort: they are copied from the originating
 * attendance when that attendance recorded them, and a sale opened from one
 * that did not will legitimately have none. That is shown as an explained
 * absence rather than an error, because nothing is wrong with the record.
 *
 * @param props.buyerName - The buying client's resolved name, if any.
 * @param props.sellers - The selling parties, possibly none.
 * @returns The rendered parties panel.
 */
export function SalePartiesPanel({ buyerName, sellers = [] }: SalePartiesPanelProps) {
  const { t } = useTranslation('features/sales');

  return (
    <SurfaceCard
      testId="sale-parties-panel"
      className="flex flex-col gap-(--space-4)"
    >
      <div className="flex flex-col gap-(--space-2)">
        <MicroLabel>{t('detail.parties.buyer')}</MicroLabel>
        {buyerName ? (
          <PartyRow
            name={buyerName}
            role={t('detail.parties.buyer')}
            showShare={false}
          />
        ) : (
          <PanelEmptyState message={t('detail.parties.noBuyer')} />
        )}
      </div>

      <div className="flex flex-col gap-(--space-2)">
        <MicroLabel>{t('detail.parties.sellers')}</MicroLabel>
        {sellers.length === 0 ? (
          <PanelEmptyState
            message={t('detail.parties.noSellers')}
            hint={t('detail.parties.noSellersHint')}
          />
        ) : (
          sellers.map((seller, index) => (
            <PartyRow
              // Keyed by the client id: stable and unique, unlike a name,
              // which two co-owners can legitimately share.
              key={seller.client_id || index}
              name={seller.name}
              shareBps={seller.share_bps}
              role={t('detail.parties.sellers')}
              showShare
            />
          ))
        )}
      </div>
    </SurfaceCard>
  );
}

/**
 * One party: initials, name, and the detail that identifies them.
 *
 * @param props.party - The party record.
 * @param props.role - The localized role label.
 * @param props.showShare - Whether the co-ownership share is meaningful.
 * @returns The rendered row.
 */
function PartyRow({
  name,
  shareBps,
  role,
  showShare,
}: {
  /** Resolved by the server; empty when the client no longer resolves. */
  name?: string;
  shareBps?: number;
  role: string;
  showShare: boolean;
}) {
  const { t } = useTranslation('features/sales');

  // The CPF used to sit here, read off the snapshot the sale carried. The
  // sale stores no document any more, and resolving one purely to print it
  // would put a person's CPF on a screen that never needed it.
  const meta = showShare ? shareLabel(shareBps, t) : '';

  return (
    <div className="flex items-center gap-(--space-3)" data-testid="sale-party-row">
      <Text
        component="span"
        variant="meta"
        fw={600}
        tone="secondary"
        aria-hidden="true"
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-(--radius-full)',
          'border border-(--border-subtle) bg-(--surface-tertiary)',
        )}
      >
        {initials(name)}
      </Text>
      <div className="flex min-w-0 flex-1 flex-col gap-(--space-0-5)">
        <Text
          component="span"
          variant="bodySm"
          fw={500}
          tone="primary"
          className="truncate"
        >
          {name}
        </Text>
        {meta && (
          <Text component="span" variant="meta" tone="tertiary">
            {meta}
          </Text>
        )}
      </div>
      <Text component="span" variant="meta" tone="secondary">
        {role}
      </Text>
    </div>
  );
}

/**
 * Renders a co-ownership share, which the backend stores in basis points.
 *
 * @param shareBps - The share in basis points, where 10000 is the whole.
 * @param t - The namespace translator.
 * @returns The share label, or the not-stated label when it is unset.
 */
function shareLabel(
  shareBps: number | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!shareBps) return t('detail.parties.noShare');
  return t('detail.parties.share', { percentage: shareBps / 100 });
}

/**
 * The first two initials of a name, for the avatar chip.
 *
 * Optional because the name is resolved on read: a client that no longer
 * resolves leaves the chip empty rather than crashing the panel.
 *
 * @param name - The party's name, if it resolved.
 * @returns Up to two uppercase initials.
 */
function initials(name?: string): string {
  return (name ?? '')
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export default SalePartiesPanel;
